// app/javascript/components/student/AvatarCropperModal.tsx
import React, { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ZoomIn, ZoomOut, RotateCcw, Check, Move } from 'lucide-react'

interface Props {
  isOpen: boolean
  imageSrc: string | null
  onClose: () => void
  onCropComplete: (croppedBase64: string) => void
}

export function AvatarCropperModal({ isOpen, imageSrc, onClose, onCropComplete }: Props) {
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)

  // Reset offset and zoom when a new image is loaded
  useEffect(() => {
    if (imageSrc) {
      setZoom(1)
      setOffset({ x: 0, y: 0 })
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = imageSrc
      img.onload = () => {
        imgRef.current = img
        drawCanvas()
      }
    }
  }, [imageSrc])

  useEffect(() => {
    drawCanvas()
  }, [zoom, offset])

  const drawCanvas = () => {
    const canvas = canvasRef.current
    const img = imgRef.current
    if (!canvas || !img) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const size = 320
    canvas.width = size
    canvas.height = size

    ctx.clearRect(0, 0, size, size)

    // Calculate scaling to cover 320x320 canvas
    const scale = Math.max(size / img.width, size / img.height) * zoom
    const drawWidth = img.width * scale
    const drawHeight = img.height * scale

    // Center image + offsets
    const x = (size - drawWidth) / 2 + offset.x
    const y = (size - drawHeight) / 2 + offset.y

    ctx.save()
    ctx.drawImage(img, x, y, drawWidth, drawHeight)
    ctx.restore()
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y })
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      setIsDragging(true)
      const touch = e.touches[0]
      setDragStart({ x: touch.clientX - offset.x, y: touch.clientY - offset.y })
    }
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDragging || e.touches.length !== 1) return
    const touch = e.touches[0]
    setOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    })
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
  }

  const handleSaveCrop = () => {
    const canvas = canvasRef.current
    const img = imgRef.current
    if (!canvas || !img) return

    // Create a crisp 400x400 output canvas for optimized Base64 storage
    const outCanvas = document.createElement('canvas')
    outCanvas.width = 400
    outCanvas.height = 400
    const outCtx = outCanvas.getContext('2d')
    if (!outCtx) return

    const size = 400
    const scale = Math.max(320 / img.width, 320 / img.height) * zoom
    const ratio = size / 320

    const drawWidth = img.width * scale * ratio
    const drawHeight = img.height * scale * ratio
    const x = (size - drawWidth) / 2 + offset.x * ratio
    const y = (size - drawHeight) / 2 + offset.y * ratio

    outCtx.save()
    outCtx.drawImage(img, x, y, drawWidth, drawHeight)
    outCtx.restore()

    // Export compressed JPEG (~50KB)
    const base64Data = outCanvas.toDataURL('image/jpeg', 0.85)
    onCropComplete(base64Data)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md bg-card/95 backdrop-blur-xl border-border/80 shadow-glass-lg p-4 sm:p-6 rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg font-extrabold text-foreground flex items-center gap-2">
            <Move className="h-4.5 w-4.5 text-primary" /> Crop Profile Photo
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Drag to adjust position and use slider to zoom before saving your avatar.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center space-y-4 my-2">
          {/* Canvas Viewport Container */}
          <div className="relative w-64 h-64 sm:w-80 sm:h-80 max-w-[calc(100vw-5rem)] max-h-[calc(100vw-5rem)] aspect-square rounded-2xl overflow-hidden border-2 border-primary/40 shadow-inner bg-black/40 flex items-center justify-center cursor-move touch-none">
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              className="w-full h-full"
            />
            
            {/* Circular Crop Overlay Guide */}
            <div className="absolute inset-0 rounded-full border-2 border-primary/60 pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]" />
          </div>

          {/* Zoom Slider Controls */}
          <div className="w-full flex items-center gap-2.5 px-1 sm:px-2">
            <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              type="range"
              min="0.8"
              max="3"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full accent-primary h-1.5 bg-muted rounded-lg appearance-none cursor-pointer"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }) }}
              title="Reset Position"
            >
              <RotateCcw className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2">
          <Button variant="outline" size="sm" className="rounded-xl font-bold border-border text-xs h-9 px-3" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" className="rounded-xl font-extrabold gap-1.5 bg-primary text-primary-fg hover:bg-primary/90 text-xs h-9 px-3.5" onClick={handleSaveCrop}>
            <Check className="h-3.5 w-3.5" /> Save Avatar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
